// A sound's time-stretch, its pitch kept (spec time-stretch.md): WSOLA, waveform-similarity
// overlap-add, as SoundTouch runs it. The output is built of sequences laid at a fixed stride, each
// read from the input where the speed puts it, moved within a seek window to where its start best
// continues the sequence before, and crossfaded over their overlap. Fed in chunks, it keeps under
// 70 ms of input a channel, so a long file never sits whole in memory. Pure: no DOM, no Web Audio.

/** The speeds a clip plays at (spec clip-speed.md decision 1): Gecko's sound plays only within them. */
export const MIN_SPEED = 0.25;
export const MAX_SPEED = 4;

/** SoundTouch's defaults, for speech and music alike: a sequence, its overlap, the seek window. */
const SEQUENCE_S = 0.04;
const OVERLAP_S = 0.008;
const SEEK_S = 0.015;
/** The coarse search's step, then the fine search's reach around its best (SoundTouch's quick seek). */
const COARSE = 4;

/**
 * How a stretch reads its input, in samples: the input sample its output's sample `out` reads, and
 * the output sample at which it reads input sample `input`. One speed is a number; a speed curve
 * (spec clip-speed-curves) is its time map, so WSOLA's read step follows the curve.
 */
export interface Pace {
  inputAt(out: number): number;
  outputAt(input: number): number;
}

export interface Stretcher {
  /** The next samples of each channel in; the output samples ready so far out, one array a channel. */
  push(input: readonly Float32Array[]): Float32Array[];
  /** The rest, once the input has ended: the output then lasts the input over the speed (or as its pace maps it), to the sample. */
  flush(): Float32Array[];
  /** The input samples it holds a channel, its storage: a window between pushes, none after the end. */
  retained(): number;
}

function joined(parts: readonly Float32Array[]): Float32Array {
  const whole = new Float32Array(parts.reduce((sum, part) => sum + part.length, 0));
  let at = 0;
  for (const part of parts) {
    whole.set(part, at);
    at += part.length;
  }
  return whole;
}

/**
 * A stretcher of `channels` channels at `sampleRate`: to `1 / speed` of the length (held to 0.25× to
 * 4×), or along a curve's `Pace`.
 */
export function stretcher(speed: number | Pace, sampleRate: number, channels: number): Stretcher {
  const rate = typeof speed === "number" ? Math.min(Math.max(speed, MIN_SPEED), MAX_SPEED) : null;
  const pace: Pace = rate === null ? (speed as Pace) : { inputAt: (out) => out * rate, outputAt: (input) => input / rate };
  const sequence = Math.round(SEQUENCE_S * sampleRate);
  const overlap = Math.round(OVERLAP_S * sampleRate);
  const stride = sequence - overlap;
  const reach = Math.round((SEEK_S * sampleRate) / 2);
  // A linear crossfade whose two sides sum to 1.
  const fadeIn = Float32Array.from({ length: overlap }, (_, at) => (at + 0.5) / overlap);
  /** The input still needed, from the absolute sample `heldFrom`. */
  let held: Float32Array[] = Array.from({ length: channels }, () => new Float32Array(0));
  let heldFrom = 0;
  let received = 0;
  let frame = 0;
  let emitted = 0;
  /** The last sequence's end, which the next one's start crossfades over; null before the first. */
  let tail: Float32Array[] | null = null;

  /** Input sample `at` of channel `channel`: 0 past what came in, as at the end of a flush. */
  const sample = (channel: number, at: number) => held[channel]?.[at - heldFrom] ?? 0;

  /**
   * Where the sequence near `ideal` starts: its overlap most like the last tail's, by normalized
   * cross-correlation summed over the channels, so channels of opposite polarity do not cancel
   * (the AO review of #1124). One start for every channel keeps them together.
   */
  function bestStart(ideal: number, reference: readonly Float32Array[]): number {
    const low = Math.max(0, ideal - reach);
    const high = ideal + reach;
    // Each channel over every place the search reads, once.
    const windows = Array.from({ length: channels }, (_, channel) =>
      Float32Array.from({ length: high - low + overlap }, (_, at) => sample(channel, low + at)),
    );
    const score = (start: number) => {
      let dot = 0;
      let energy = 0;
      const from = start - low;
      for (let channel = 0; channel < channels; channel += 1) {
        const window = windows[channel];
        const tailOf = reference[channel];
        if (window === undefined || tailOf === undefined) continue;
        for (let at = 0; at < overlap; at += 1) {
          const value = window[from + at] ?? 0;
          dot += (tailOf[at] ?? 0) * value;
          energy += value * value;
        }
      }
      return energy === 0 ? 0 : dot / Math.sqrt(energy);
    };
    // The best by score, the nearer to the ideal on a tie.
    const better = (start: number, best: { start: number; score: number }) => {
      const found = score(start);
      const nearer = Math.abs(start - ideal) < Math.abs(best.start - ideal);
      return found > best.score || (found === best.score && nearer) ? { start, score: found } : best;
    };
    let best = { start: ideal, score: score(ideal) };
    for (let start = low; start <= high; start += COARSE) best = better(start, best);
    const around = best.start;
    for (let start = Math.max(low, around - COARSE + 1); start <= Math.min(high, around + COARSE - 1); start += 1) {
      best = better(start, best);
    }
    return best.start;
  }

  /** The sequences the input in hand allows (all, padded with silence, while flushing up to `upTo`). */
  function run(flushing: boolean, upTo: number): Float32Array[] {
    const out: Float32Array[][] = Array.from({ length: channels }, () => []);
    for (;;) {
      if (flushing && emitted >= upTo) break;
      const ideal = Math.round(pace.inputAt(frame * stride));
      // Before the end, a sequence needs its input in, and ends within the output the input so far gives.
      if (!flushing && (ideal + reach + sequence > received || (frame + 1) * stride > Math.floor(pace.outputAt(received)))) break;
      const start = tail === null ? ideal : bestStart(ideal, tail);
      const next: Float32Array[] = [];
      for (let channel = 0; channel < channels; channel += 1) {
        const piece = new Float32Array(stride);
        const before = tail?.[channel];
        for (let at = 0; at < stride; at += 1) {
          const value = sample(channel, start + at);
          const fade = at < overlap && before !== undefined ? (fadeIn[at] ?? 1) : 1;
          piece[at] = before !== undefined && at < overlap ? (before[at] ?? 0) * (1 - fade) + value * fade : value;
        }
        out[channel]?.push(piece);
        next.push(Float32Array.from({ length: overlap }, (_, at) => sample(channel, start + stride + at)));
      }
      tail = next;
      frame += 1;
      emitted += stride;
    }
    // What the next sequence can no longer reach goes, copied out of the pushed chunk's storage so
    // that storage is let go (the AO review of #1124).
    const drop = Math.min(Math.round(pace.inputAt(frame * stride)) - reach, received) - heldFrom;
    if (drop > 0) {
      held = held.map((channel) => channel.slice(Math.min(drop, channel.length)));
      heldFrom += drop;
    }
    return out.map(joined);
  }

  return {
    push(input) {
      held = held.map((channel, at) => joined([channel, input[at] ?? new Float32Array(0)]));
      received += input[0]?.length ?? 0;
      // At 1× the output is the input itself.
      if (rate === 1) {
        const out = held;
        held = held.map(() => new Float32Array(0));
        heldFrom = received;
        emitted = received;
        return out;
      }
      return run(false, 0);
    },
    flush() {
      if (rate === 1) return Array.from({ length: channels }, () => new Float32Array(0));
      const target = Math.round(pace.outputAt(received));
      const before = emitted;
      const rest = run(true, target);
      held = held.map(() => new Float32Array(0));
      // The last sequence ends past the target: the output lasts the input over the speed.
      return rest.map((channel) => channel.slice(0, Math.max(0, target - before)));
    },
    retained() {
      return (held[0]?.buffer.byteLength ?? 0) / Float32Array.BYTES_PER_ELEMENT;
    },
  };
}

/** A whole sound stretched at once, one array a channel. */
export function stretch(input: readonly Float32Array[], speed: number, sampleRate: number): Float32Array[] {
  const stretching = stretcher(speed, sampleRate, input.length);
  const first = stretching.push(input);
  const rest = stretching.flush();
  return first.map((channel, at) => joined([channel, rest[at] ?? new Float32Array(0)]));
}
